// How to play (user, 2026-09-27: every game gets a tutorial page covering
// its functions and odds). One sheet shared by every table, opened from the
// "?" in the table's top bar. The pays and chances are worked out from the
// engine's own constants and formulas wherever the engine has them, so the
// page can't drift from the game; the return figures that can only be
// measured by sim (Blackjack, Hold'em, ...) are the recorded ones from the
// glasses repo's CLAUDE.md, rounded and marked "about".
import type { ReactNode } from 'react';
import { betLadder } from '../../engine/casino';
import { GAMES } from '../../engine/games';
import { TIE_PAYS } from '../../engine/games/baccarat';
import { SEGMENTS, WHEEL_SIZE } from '../../engine/games/bigSix';
import { TIE_BONUS } from '../../engine/games/casinoWar';
import { EDGE as CRASH_EDGE, JACKPOT_MULTIPLIER } from '../../engine/games/crash';
import { diceMultiplier, EDGE as DICE_EDGE, winChance } from '../../engine/games/dice';
import { EDGE as HILO_EDGE } from '../../engine/games/highLow';
import { HOLDEM_TUNING } from '../../engine/games/holdem';
import { HORSES, horseOdds, TAKEOUT } from '../../engine/games/horseRacing';
import { DICE_EACH, RAKE as LIARS_RAKE } from '../../engine/games/liarsDice';
import { LUCK_RTP } from '../../engine/games/luck';
import { MAHJONG_TUNING } from '../../engine/games/mahjong';
import { CELLS, EDGE as MINES_EDGE, MINE_COUNTS, minesMultiplier, minesTarget } from '../../engine/games/mines';
import { plinkoTable, SLOT_ODDS, TARGET_RTP as PLINKO_RTP } from '../../engine/games/plinko';
import { PRIZE_TABLE } from '../../engine/games/scratchers';
import { slotPayout, type SlotSymbol } from '../../engine/games/slots';
import { ANTE_BONUS } from '../../engine/games/threeCardPoker';
import { EDGE as TOWER_EDGE, ROWS as TOWER_ROWS, towerMultiplier } from '../../engine/games/tower';
import type { GameId } from '../../engine/games/types';
import { PAYTABLE } from '../../engine/games/videoPoker';
import { FAN_PAY, LIMIT_FAN, MIN_FAN } from '../../engine/mahjongTiles';
import { ThreeCardRank } from '../../engine/poker';
import { money } from '../../format';
import { BALLS, FEVER_PAYS, P_FEVER, P_START, P_TULIP, TARGET_RTP as PACHINKO_RTP, TULIP_PAYS } from '../../webGames/pachinko';

/** "48.6%", "2.7%", "0.05%" */
function pct(p: number): string {
  const v = p * 100;
  return `${v >= 10 ? v.toFixed(1) : v >= 1 ? v.toFixed(1) : v >= 0.1 ? v.toFixed(2) : v.toFixed(3)}%`.replace(/\.0+%$/, '%');
}
/** "1 in 21" */
const oneIn = (p: number) => `1 in ${Math.round(1 / p).toLocaleString('en-US')}`;
/** "×1.96" */
const times = (m: number) => `×${m >= 100 ? Math.round(m).toLocaleString('en-US') : m.toFixed(2).replace(/\.00$/, '')}`;

interface OddsTable {
  cols: string[];
  rows: (string | ReactNode)[][];
  /** Rows to set in brass (a jackpot row, the top prize). */
  hot?: number[];
}

interface HowTo {
  /** The aim, in one or two sentences. */
  goal: string;
  /** How a play goes on this screen, in order. */
  steps: string[];
  /** Pays and chances. */
  tables?: { title?: string; table: OddsTable }[];
  /** Short facts under the tables. */
  notes?: string[];
  /** What the table gives back on average, per bet: "96%", "about 99.5%". */
  returns: string;
  /** What returns depends on, if anything ("with basic strategy"). */
  returnsNote?: string;
  /** The ★ House Special's return, when it's not the usual 103% / 110%. */
  star?: string | null;
}

const STAR = `about ${pct(LUCK_RTP[1])} as the ★ House Special, ${pct(LUCK_RTP[2])} with Liquid Courage while Tipsy`;

const SLOT_ROWS: [string, SlotSymbol[]][] = [
  ['7 7 7', ['SEV', 'SEV', 'SEV']],
  ['BAR BAR BAR', ['BAR', 'BAR', 'BAR']],
  ['Three bells', ['BEL', 'BEL', 'BEL']],
  ['Three cherries', ['CHR', 'CHR', 'CHR']],
  ['Three lemons', ['LEM', 'LEM', 'LEM']],
  ['Any mix of 7, BAR, bell', ['SEV', 'BAR', 'BEL']],
  ['Two cherries', ['CHR', 'CHR', 'BLK']],
  ['One cherry', ['CHR', 'BLK', 'BLK']],
];

// Three Card hands: chance of being dealt each (52-card deck, 22,100 hands).
const THREE_CARD_HANDS: [string, ThreeCardRank | null, number][] = [
  ['Straight flush', ThreeCardRank.StraightFlush, 48 / 22100],
  ['Three of a kind', ThreeCardRank.ThreeOfAKind, 52 / 22100],
  ['Straight', ThreeCardRank.Straight, 720 / 22100],
  ['Flush', null, 1096 / 22100],
  ['Pair', null, 3744 / 22100],
];

// Jacks or Better with good play: how often each hand comes up after the
// draw (the standard 9/6 figures).
const VP_ODDS: [keyof typeof PAYTABLE, string, number][] = [
  ['royalFlush', 'Royal flush', 1 / 40391],
  ['straightFlush', 'Straight flush', 1 / 9148],
  ['fourOfAKind', 'Four of a kind', 1 / 423],
  ['fullHouse', 'Full house', 1 / 87],
  ['flush', 'Flush', 1 / 91],
  ['straight', 'Straight', 1 / 89],
  ['threeOfAKind', 'Three of a kind', 1 / 13.4],
  ['twoPair', 'Two pair', 1 / 7.7],
  ['jacksOrBetter', 'Jacks or better', 1 / 4.7],
];

// Craps: the chance of each total on two dice.
const WAYS = [0, 0, 1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1];
const dice2 = (...totals: number[]) => totals.reduce((s, t) => s + WAYS[t]!, 0) / 36;
/** Pass wins: 7/11 on the come-out, or a point made before a 7. */
const PASS_WIN = dice2(7, 11) + [4, 5, 6, 8, 9, 10].reduce((s, p) => s + dice2(p) * (dice2(p) / (dice2(p) + dice2(7))), 0);

/** Six decks: after your card, 23 of the other 311 match its rank. */
const WAR_TIE = 23 / 311;
const scratchReturn = PRIZE_TABLE.reduce((s, [m, p]) => s + m * p, 0);
const scratchWin = PRIZE_TABLE.reduce((s, [, p]) => s + p, 0);
const pachinkoFever = 1 - (1 - P_START * P_FEVER) ** BALLS;
/** A START spin shows REACH when its first two reels match: every FEVER,
 *  plus 1 in 9 of the other spins (spin() in webGames/pachinko.ts). */
const PACHINKO_REACH = P_FEVER + (1 - P_FEVER) / 9;

export const HOW_TO: Record<string, HowTo> = {
  blackjack: {
    goal: 'Get closer to 21 than the dealer without going over.',
    steps: [
      'Set your bet with − and +, then Deal. You and the dealer get two cards; one of the dealer’s is face down.',
      'Hit for another card, or Stand. Double doubles your bet for exactly one more card. Split turns a pair into two hands (once).',
      'Number cards count their number, faces count 10, aces 1 or 11.',
      'The dealer then draws to 17 and stands on all 17s. Closer to 21 wins; over 21 busts.',
    ],
    tables: [
      {
        table: {
          cols: ['Result', 'Pays', 'Chance'],
          rows: [
            ['Blackjack (ace + a 10 as your first two)', '3:2', oneIn(0.0475)],
            ['Beat the dealer', '1:1', ''],
            ['Tie', 'your bet back', ''],
          ],
        },
      },
    ],
    notes: ['Six decks. The dealer checks for blackjack first. No insurance or surrender.'],
    returns: 'about 99.5%',
    returnsNote: 'with basic strategy; guessing does worse',
  },
  slots: {
    goal: 'Line up symbols on any of the five lines.',
    steps: [
      'Set your bet, then Spin (or pull the lever). Your bet is split across five lines: the three rows and the two diagonals.',
      'Each line pays on its own, so one spin can win on several lines.',
      'Tap Pays any time to see the pay glass.',
    ],
    tables: [
      {
        title: 'Per line (a fifth of your bet)',
        table: { cols: ['Line', 'Pays'], rows: SLOT_ROWS.map(([name, line]) => [name, times(slotPayout(line).multiplier)]), hot: [0] },
      },
    ],
    returns: 'about 97%',
  },
  scratchers: {
    goal: 'Scratch the ticket; three matching amounts win that amount.',
    steps: ['Buy a ticket (the price is fixed on each floor).', 'Rub the six spots with your finger. One swipe can scratch them all.', 'The ticket is decided when you buy it, like a real one.'],
    tables: [
      {
        table: {
          cols: ['Match three of', 'Chance'],
          rows: PRIZE_TABLE.map(([m, p]) => [m === 1 ? '1× (your money back)' : `${m}× the ticket`, oneIn(p)]),
          hot: [0],
        },
      },
    ],
    notes: [`About ${oneIn(scratchWin).replace('1 in ', '1 ticket in ')} wins something.`],
    returns: pct(scratchReturn),
    star: null,
  },
  crash: {
    goal: 'Cash out before the rocket crashes.',
    steps: [
      'Set your bet and Launch. The multiplier climbs from ×1.00 (it doubles about every 6 seconds).',
      'Tap Cash out to win your bet times the multiplier. If it crashes first, the bet is lost.',
      'Auto cashes out for you at a target you pick, which is the sure way to hit a number.',
    ],
    tables: [
      {
        table: {
          cols: ['Cash out at', 'Chance it gets there'],
          rows: [1.5, 2, 3, 5, 10, JACKPOT_MULTIPLIER].map((m) => [times(m), pct((1 - CRASH_EDGE) / m)]),
        },
      },
    ],
    notes: [`About ${pct(CRASH_EDGE)} of rockets crash at ×1.00. Every target returns the same on average.`],
    returns: pct(1 - CRASH_EDGE),
  },
  baccarat: {
    goal: 'Bet on the hand that ends closest to 9: Player or Banker, or a Tie.',
    steps: [
      'Pick Player, Banker or Tie, set your bet, then Deal.',
      'Each hand gets two cards, and a third by fixed rules. Only the last digit counts (7 + 8 = 5). Tens and faces count 0.',
      'You don’t make any choices after the deal.',
    ],
    tables: [
      {
        table: {
          cols: ['Bet', 'Pays', 'Wins'],
          rows: [
            ['Player', '1:1', pct(0.446247)],
            ['Banker', '1:1 (a Banker 6 pays 1:2)', pct(0.458597)],
            ['Tie', `${TIE_PAYS}:1`, pct(0.095156)],
          ],
        },
      },
    ],
    notes: ['A tie gives Player and Banker bets back.'],
    returns: 'about 98.8% (Player), 98.5% (Banker), 86% (Tie)',
  },
  highlow: {
    goal: 'Call whether the next card is higher or lower, as many times as you dare.',
    steps: [
      'Deal a card. Guess Higher or Lower; each right guess multiplies your stake.',
      'Cash out any time to keep it. One wrong guess loses it all. The same rank loses too.',
      'Aces are high. The deck is endless, so every rank is equally likely every time.',
    ],
    tables: [
      {
        title: 'First guess from a card showing',
        table: {
          cols: ['Showing', 'Higher', 'Lower'],
          rows: [
            ['2', `${pct(12 / 13)} · ${times((1 - HILO_EDGE) / (12 / 13))}`, 'can’t win'],
            ['5', `${pct(9 / 13)} · ${times((1 - HILO_EDGE) / (9 / 13))}`, `${pct(3 / 13)} · ${times((1 - HILO_EDGE) / (3 / 13))}`],
            ['8', `${pct(6 / 13)} · ${times((1 - HILO_EDGE) / (6 / 13))}`, `${pct(6 / 13)} · ${times((1 - HILO_EDGE) / (6 / 13))}`],
            ['J', `${pct(3 / 13)} · ${times((1 - HILO_EDGE) / (3 / 13))}`, `${pct(9 / 13)} · ${times((1 - HILO_EDGE) / (9 / 13))}`],
          ],
        },
      },
    ],
    notes: ['The long shot pays more; the safe call pays little. The house takes its edge once, on the first guess, so cashing out after any streak returns the same on average.'],
    returns: pct(1 - HILO_EDGE),
  },
  liarsdice: {
    goal: 'Be the last player with dice. Last one standing takes the pot.',
    steps: [
      `Everyone antes your bet and rolls ${DICE_EACH} dice in secret. 1s are wild: they count as any face.`,
      'On your turn, raise the bid (“four 5s” means at least four dice on the whole table show 5 or 1), or call Liar! on the last bid.',
      'On a call the dice are shown. If the bid was short, the bidder loses a die; if it holds, the caller does.',
      'You play Rosa (careful) and Vic (bluffs more).',
    ],
    notes: [
      'Each die you can’t see shows a given face (or a wild 1) one time in three, so on average a third of the hidden dice match any bid.',
      'The screen shows how likely the current bid is to be a lie.',
      `The winner takes the pot less ${pct(LIARS_RAKE)}.`,
    ],
    returns: 'about 99%',
    returnsNote: 'with careful play; a skill game',
  },
  mines: {
    goal: 'Uncover safe tiles; each one raises your payout. Hit a mine and it’s gone.',
    steps: [`Pick how many mines hide on the ${CELLS}-tile board, set your bet and Start.`, 'Tap tiles to reveal them. Cash out any time to keep the current payout.', 'More mines: each safe tile pays more, and more is at risk.'],
    tables: [
      {
        title: 'Payout after safe tiles',
        table: {
          cols: ['Mines', '1st tile safe', 'After 1', 'After 3', 'After 5'],
          rows: MINE_COUNTS.map((m) => [String(m), pct((CELLS - m) / CELLS), times(minesMultiplier(m, 1)), times(minesMultiplier(m, 3)), times(minesMultiplier(m, 5))]),
        },
      },
    ],
    notes: ['Every stopping point returns the same on average; it’s your call how far to push.'],
    returns: pct(1 - MINES_EDGE),
    star: `about ${pct(minesTarget(1))} as the ★ House Special, ${pct(minesTarget(2))} with Liquid Courage while Tipsy`,
  },
  roulette: {
    goal: 'Bet where the ball will land on a single-zero wheel.',
    steps: ['Tap a spot on the layout to choose your bet, or open Numbers for a single number.', 'Set your bet and Spin. One bet per spin.', 'The zero is green: every outside bet loses on it.'],
    tables: [
      {
        table: {
          cols: ['Bet', 'Pays', 'Wins'],
          rows: [
            ['Red, Black, Odd, Even, 1–18, 19–36', '1:1', pct(18 / 37)],
            ['1st, 2nd or 3rd dozen', '2:1', pct(12 / 37)],
            ['A single number', '35:1', pct(1 / 37)],
          ],
        },
      },
    ],
    returns: pct(36 / 37),
  },
  craps: {
    goal: 'Bet on the dice: Pass, Don’t Pass, or the one-roll Field.',
    steps: [
      'Pick a bet on the layout, set your bet and Roll.',
      'Pass: the first roll wins on 7 or 11 and loses on 2, 3 or 12. Any other total becomes the point; keep rolling until the point (win) or a 7 (lose).',
      'Don’t Pass is the mirror image, and a 12 on the first roll is a push.',
      'Field is settled in one roll.',
    ],
    tables: [
      {
        table: {
          cols: ['Bet', 'Pays', 'Wins'],
          rows: [
            ['Pass', '1:1', pct(PASS_WIN)],
            ['Don’t Pass', '1:1 (12 first roll pushes)', pct(1 - PASS_WIN - dice2(12))],
            ['Field: 3, 4, 9, 10, 11', '1:1', pct(dice2(3, 4, 9, 10, 11))],
            ['Field: 2 or 12', '2:1', pct(dice2(2, 12))],
          ],
        },
      },
    ],
    notes: ['While a point is on, you roll until it’s decided.'],
    returns: 'about 98.6% (Pass and Don’t Pass), 94.4% (Field)',
  },
  threecard: {
    goal: 'Beat the dealer’s three-card hand.',
    steps: [
      'Set your ante and deal. Look at your three cards.',
      'Play (put up the same again) or Fold (lose the ante). Play queen-six-four or better.',
      'The dealer needs queen-high to qualify. If not, your ante pays 1:1 and your play bet comes back.',
      'If the dealer qualifies, the better hand wins both bets at 1:1.',
    ],
    tables: [
      {
        title: 'Hands, best first, and the ante bonus',
        table: {
          cols: ['Hand', 'Ante bonus', 'Dealt'],
          rows: THREE_CARD_HANDS.map(([name, rank, p]) => [name, rank && ANTE_BONUS[rank] ? `${ANTE_BONUS[rank]}:1` : '', oneIn(p)]),
        },
      },
    ],
    notes: ['A played hand collects the ante bonus whatever the dealer has. In three-card poker a straight beats a flush.'],
    returns: 'about 96.6% of the ante',
    returnsNote: 'playing Q-6-4 or better',
  },
  holdem: {
    goal: 'Win the pot with the best five-card hand, or by making everyone else fold.',
    steps: [
      'You play Mae (tight) and Sal (loose, bluffs). Your bet is the big blind; the small blind is half.',
      'You get two cards; five shared cards come out over the flop (3), turn and river.',
      'On your turn: Fold, Check or Call, or Bet or Raise. Bets are one blind before the turn and two after, up to four a round.',
      'The screen shows your chance to win and what a call needs to break even.',
    ],
    tables: [
      {
        title: 'Hands, best first',
        table: {
          cols: ['Hand'],
          rows: ['Royal flush', 'Straight flush', 'Four of a kind', 'Full house', 'Flush', 'Straight', 'Three of a kind', 'Two pair', 'Pair', 'High card'].map((h) => [h]),
        },
      },
    ],
    notes: [`You win the part of the pot you can cover, less ${pct(HOLDEM_TUNING.rake)} once a flop is dealt. No flop, no rake.`],
    returns: 'about 99%',
    returnsNote: 'with solid play; a skill game',
  },
  bigsix: {
    goal: 'Pick a segment; if the wheel stops on it, you win about its face value.',
    steps: ['Tap a segment to bet on it, set your bet, and Spin.'],
    tables: [
      {
        table: {
          cols: ['Segment', 'Pays', 'On the wheel', 'Chance'],
          rows: SEGMENTS.map((s) => [s.label, `${s.pays}:1`, `${s.count} of ${WHEEL_SIZE}`, pct(s.count / WHEEL_SIZE)]),
          hot: [SEGMENTS.length - 2, SEGMENTS.length - 1],
        },
      },
    ],
    returns: 'about 96% on every segment',
  },
  war: {
    goal: 'Higher card wins. Aces are high.',
    steps: [
      'Set your bet and Deal: one card each.',
      'Higher card wins 1:1. On a tie, Go to war (put up the same again) or Surrender (get half your bet back).',
      `In a war, beat the dealer and you win the raise, and your first bet comes back. Tie again and you win the raise plus ${TIE_BONUS}× your bet.`,
    ],
    tables: [
      {
        table: {
          cols: ['Deal', 'Chance'],
          rows: [
            ['You win', pct((1 - WAR_TIE) / 2)],
            ['Dealer wins', pct((1 - WAR_TIE) / 2)],
            ['Tie (war or surrender)', pct(WAR_TIE)],
          ],
        },
      },
    ],
    notes: ['Six decks. Going to war is better than surrendering.'],
    returns: 'about 97%',
    returnsNote: 'always going to war',
  },
  dice: {
    goal: 'Pick a target; win if the roll lands over (or under) it.',
    steps: ['Tap Target to pick 5 to 95, and Win if to choose over or under.', 'Set your bet and Roll. The roll is 0.00 to 99.99.', 'A likely win pays little; a long shot pays a lot.'],
    tables: [
      {
        table: {
          cols: ['Win if', 'Chance', 'Pays'],
          rows: ([[95, 'under'], [75, 'under'], [50, 'under'], [25, 'under'], [10, 'under'], [95, 'over']] as const).map(([t, d]) => [`${d} ${t}`, pct(winChance(t, d)), times(diceMultiplier(t, d))]),
        },
      },
    ],
    notes: ['Every target has the same edge.'],
    returns: pct(1 - DICE_EDGE),
  },
  videopoker: {
    goal: 'Make the best poker hand in one draw. Jacks or Better.',
    steps: ['Set your bet and Deal five cards.', 'Tap the cards you want to keep (they show HELD), then DRAW to replace the rest.', 'The hand pays by the table on the machine’s glass.'],
    tables: [
      {
        table: {
          cols: ['Hand', 'Pays', 'Comes up'],
          rows: VP_ODDS.map(([k, name, p]) => [name, times(PAYTABLE[k]), oneIn(p)]),
          hot: [0],
        },
      },
    ],
    notes: ['“Pays” is the whole return for your bet: ×1 is your money back. Chances are with good play.'],
    returns: 'about 98%',
    returnsNote: 'with perfect play; realistic play is a little lower',
  },
  plinko: {
    goal: 'Drop a ball through the pegs. The middle is likely and pays little; the edges are rare and pay big.',
    steps: ['Pick a Risk (Low, Medium, High), set your bet and Drop.', 'The ball bounces left or right at each of 8 rows into one of 9 slots.'],
    tables: [
      {
        title: 'Slots from the middle out',
        table: {
          cols: ['Slot', 'Chance', 'Low', 'Medium', 'High'],
          rows: [4, 3, 2, 1, 0].map((k) => [
            k === 4 ? 'Middle' : k === 0 ? 'Edge' : `${4 - k} out`,
            pct(SLOT_ODDS[k]! * (k === 4 ? 1 : 2)),
            times(plinkoTable('low')[k]!),
            times(plinkoTable('medium')[k]!),
            times(plinkoTable('high')[k]!),
          ]),
          hot: [4],
        },
      },
    ],
    notes: ['Chances count both sides. Every risk level returns the same on average; High swings more.'],
    returns: pct(PLINKO_RTP),
  },
  tower: {
    goal: 'Climb the tower one door at a time. Cash out before a trap.',
    steps: [
      `Pick Easy (1 trap in 3 doors) or Hard (2 traps), set your bet and Start climbing. ${TOWER_ROWS} rows.`,
      'Tap a door on the row you’re on. A safe door raises your payout; a trap loses it.',
      'Cash out any time.',
    ],
    tables: [
      {
        title: 'Payout after rows',
        table: {
          cols: ['Rows climbed', 'Easy', 'Hard'],
          rows: [1, 2, 4, TOWER_ROWS].map((r) => [String(r), `${times(towerMultiplier('easy', r))} · ${pct((2 / 3) ** r)}`, `${times(towerMultiplier('hard', r))} · ${pct((1 / 3) ** r)}`]),
        },
      },
    ],
    notes: ['Each cell shows the payout and the chance of getting that far.'],
    returns: pct(1 - TOWER_EDGE),
  },
  pachinko: {
    goal: 'Fire a stream of balls through the pins and hope for FEVER.',
    steps: [
      `Set your bet and Fire ${BALLS} balls; each ball is worth a ${BALLS}th of your bet.`,
      `A ball in a tulip pays ${TULIP_PAYS} balls. One in START pays 1 and spins the screen.`,
      'The screen has three reels of numbers, 1 to 9. They spin and stop left to right. The numbers themselves don’t matter; only matching does.',
      'If the first two stop on the same number, it’s a REACH!: the last reel keeps spinning a little longer while you wait to see if it matches too.',
      `Three alike is FEVER: the gate opens and ${FEVER_PAYS} balls rain into the tray. Any three alike counts; 7 7 7 pays the same as 3 3 3.`,
    ],
    tables: [
      {
        title: 'Each ball',
        table: {
          cols: ['Lands in', 'Chance', 'Pays'],
          rows: [
            ['Tulip', pct(P_TULIP), `${TULIP_PAYS} balls`],
            ['START (spins the screen)', pct(P_START), '1 ball'],
            ['FEVER (three alike on a spin)', pct(P_FEVER) + ' of spins', `${FEVER_PAYS} balls`],
          ],
          hot: [2],
        },
      },
      {
        title: 'Each START spin',
        table: {
          cols: ['On the screen', 'Chance'],
          rows: [
            ['REACH! (the first two match)', pct(PACHINKO_REACH)],
            ['A REACH that turns into FEVER', pct(P_FEVER / PACHINKO_REACH)],
            ['FEVER', pct(P_FEVER)],
          ],
          hot: [2],
        },
      },
    ],
    notes: [`About ${pct(pachinkoFever)} of plays hit at least one FEVER.`],
    returns: pct(PACHINKO_RTP),
  },
  horses: {
    goal: 'Back a horse to win. Fixed odds, six runners.',
    steps: ['Tap a horse to back it, set your bet and Race.'],
    tables: [
      {
        table: {
          cols: ['Horse', 'Wins', 'Pays'],
          rows: HORSES.map((h, i) => [h.name, pct(h.chance), times(horseOdds(i))]),
          hot: [HORSES.length - 1],
        },
      },
    ],
    notes: [`“Pays” includes your stake. The track keeps ${pct(TAKEOUT)} of every price.`],
    returns: pct(1 - TAKEOUT),
  },
  mahjong: {
    goal: `Hong Kong mahjong against Lee, Wu and Kim. Make four sets and a pair worth at least ${MIN_FAN} fan.`,
    steps: [
      'Tap a tile to pick it up, and tap it again to throw it. Your drawn tile sits apart on the right.',
      'Sets are pungs (three alike), chows (three in a row, one suit) and kongs (four alike). You need four sets and a pair.',
      'When someone throws a tile you can use, buttons appear: Mahjong!, Pung, Chow (from the player on your left only), Kong, or Pass.',
      'A kong draws you a replacement tile, and so does a flower or season, which is set aside by itself.',
      'Win on your own draw with Mahjong!, or give up a hand for one bet.',
    ],
    tables: [
      {
        title: 'Pay by fan (your bet is a 3-fan hand)',
        table: {
          cols: ['Fan', 'Pays'],
          rows: Object.entries(FAN_PAY).map(([f, b]) => [Number(f) === LIMIT_FAN ? `${f} (limit)` : f, `${b} bet${b > 1 ? 's' : ''}`]),
          hot: [Object.keys(FAN_PAY).length - 1],
        },
      },
      {
        title: 'Fan',
        table: {
          cols: ['For', 'Fan'],
          rows: [
            ['Full flush (one suit)', '7'],
            ['Small dragons', '5'],
            ['Seven pairs', '4'],
            ['Half flush (one suit + honors)', '3'],
            ['All pungs', '3'],
            ['Each dragon pung; seat or round wind pung', '1'],
            ['All chows; self-drawn; concealed', '1'],
            ['Own flower, own season, or no flowers', '1'],
            ['All four flowers or seasons', '2'],
            ['Win on a kong’s replacement tile', '1'],
            ['Limit hands: big dragons, big winds, all honors, thirteen orphans, four kongs', String(LIMIT_FAN)],
          ],
        },
      },
    ],
    notes: [
      'A win off a discard is paid by whoever threw it; a self-drawn win by all three players.',
      'You pay when your discard wins for someone, or when someone self-draws. A win between two others costs you nothing.',
      `The house keeps ${pct(MAHJONG_TUNING.rake)} of what you win.`,
    ],
    returns: 'about 99–101%',
    returnsNote: 'playing solidly and claiming what helps',
  },
};

/** Tutorial pages you've already seen, per device (a convenience only). */
const SEEN_KEY = 'feelin-lucky:howto-seen';
export function seenHowTo(id: GameId): boolean {
  try {
    return (JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]).includes(id);
  } catch {
    return true; // storage blocked: don't pop the page up every visit
  }
}
export function markHowToSeen(id: GameId): void {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[];
    if (!seen.includes(id)) localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, id]));
  } catch {
    /* storage blocked */
  }
}

function Table({ table }: { table: OddsTable }) {
  return (
    <div className="howto-table-wrap">
      <table className="howto-table">
        <thead>
          <tr>{table.cols.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className={table.hot?.includes(i) ? 'hot' : ''}>
              {r.map((c, j) => <td key={j}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The How to play sheet for one game, over the table. */
export function HowToPlay({ id, floor, onClose }: { id: GameId; floor: number; onClose: () => void }) {
  const game = GAMES[id];
  const h = HOW_TO[id];
  const ladder = betLadder(id, floor);
  const star = h?.star === undefined ? STAR : h.star;
  return (
    <div className="howto" role="dialog" aria-label={`How to play ${game.name}`}>
      <div className="howto-scroll">
        <article className="howto-ticket">
          <header className="howto-head">
            <span className="howto-kicker">How to play</span>
            <h2>{game.name}</h2>
            {h && <p className="howto-goal">{h.goal}</p>}
          </header>
          {h ? (
            <>
              <section>
                <h3>Playing</h3>
                <ol className="howto-steps">
                  {h.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
                {ladder.length > 0 && (
                  <p className="howto-limits">
                    Bets on this floor: {money(ladder[0]!)} to {money(ladder[ladder.length - 1]!)}.
                  </p>
                )}
              </section>
              {h.tables && (
                <section>
                  <h3>Pays and odds</h3>
                  {h.tables.map((t, i) => (
                    <div key={i} className="howto-block">
                      {t.title && <h4>{t.title}</h4>}
                      <Table table={t.table} />
                    </div>
                  ))}
                  {h.notes?.map((n) => <p key={n} className="howto-note">{n}</p>)}
                </section>
              )}
              {!h.tables && h.notes && (
                <section>
                  <h3>Odds</h3>
                  {h.notes.map((n) => <p key={n} className="howto-note">{n}</p>)}
                </section>
              )}
              <section className="howto-return">
                <h3>Pays back</h3>
                <p>
                  <b>{h.returns}</b> of what you bet, on average{h.returnsNote ? `, ${h.returnsNote}` : ''}. The rest is the house’s edge.
                </p>
                {star && <p className="howto-star">★ {star[0]!.toUpperCase() + star.slice(1)}.</p>}
              </section>
            </>
          ) : (
            <p className="howto-goal">{game.blurb}</p>
          )}
          <section className="howto-extras">
            <div>
              <span className="howto-label">★ House Special</span>
              <p>{game.specialPerk}</p>
            </div>
            <div>
              <span className="howto-label">Jackpot</span>
              <p>{game.jackpotRule} It pays the floor’s jackpot pot.</p>
            </div>
          </section>
        </article>
      </div>
      <button type="button" className="btn btn-primary howto-close" onClick={onClose}>
        Got it
      </button>
    </div>
  );
}
